import React from 'react';
import {
    Formalite,
    FormalityValidator,
} from '@europrocurement/l2d-domain/reducers/formalites/slices/formalite';
import { Box, Card } from '@mui/material';
import { useSelector } from 'react-redux';
import { dossierValidatorsSelector } from '@europrocurement/l2d-domain';
import { InformationChip } from '@europrocurement/flexy-components';
import NoResultPreview from '../../components/NoResultPreview';
import TableRowActions from '../../components/TableRowActions';
import StaticDatatable from '../../components/StaticDatable';

type DisplayedValidator = {
    id: number;
    label: string;
    typeId: number;
    typeLabel: string;
    default: boolean;
};

type FormalityValidatorsCardProps = {
    formality: Formalite;
    onUpdateValidators: (selectedItems: FormalityValidator[]) => void;
    onShowItem: (item: FormalityValidator) => void;
};

const FormalityValidatorsCard: React.FunctionComponent<FormalityValidatorsCardProps> = function (
    props,
) {
    const { formality, onUpdateValidators, onShowItem } = props;
    const availableValidators = useSelector(dossierValidatorsSelector).data;
    const formalityValidators = formality.validators ? formality.validators : [];
    const rows: DisplayedValidator[] = formalityValidators
        .map((fv) => {
            const validator = availableValidators.find(
                (v) => (fv.validator as unknown as number) === (v.id as unknown as number),
            );

            return {
                id: validator?.id as unknown as number,
                label: validator?.label as unknown as string,
                typeId: validator?.typeId as unknown as number,
                typeLabel: validator?.typeLabel as unknown as string,
                default: fv.default as boolean,
            };
        })
        .sort((a: DisplayedValidator, b: DisplayedValidator) => {
            const aLabel = a.label as unknown as string;
            const bLabel = b.label as unknown as string;

            if (aLabel.toLowerCase() < bLabel.toLowerCase()) {
                return -1;
            }

            if (aLabel.toLowerCase() > bLabel.toLowerCase()) {
                return 1;
            }

            return 0;
        });

    const onRemoveItem = (candidate: DisplayedValidator) => {
        let validators = formalityValidators.filter(
            (item: FormalityValidator) => item.validator !== candidate.id,
        );
        // le's assure each validator category has at least one default validator
        if (candidate.default) {
            let hasDefault = false;
            validators = validators.map((formalityValidator) => {
                const validator = availableValidators.find(
                    (v) =>
                        (formalityValidator.validator as unknown as number) ===
                        (v.id as unknown as number),
                );

                if (hasDefault || validator?.typeId !== candidate.typeId) {
                    return formalityValidator;
                }

                hasDefault = true;

                return {
                    ...formalityValidator,
                    default: true,
                };
            });
        }

        onUpdateValidators(validators);
    };

    if (!rows.length) {
        return (
            <Card>
                <NoResultPreview message="Aucun valideur n'est associé à cette formalité" />
            </Card>
        );
    }

    const celStyle = {
        display: 'flex',
        alignItems: 'center',
        minHeight: '40px',
    };
    const columns = [
        {
            label: 'Nom',
            field: 'label',
            isDisplayed: true,
            render: (item: DisplayedValidator) => <Box style={celStyle}>{item.label}</Box>,
        },
        {
            label: 'Type',
            field: 'typeLabel',
            isDisplayed: true,
            render: (item: DisplayedValidator) => <Box style={celStyle}>{item.typeLabel}</Box>,
        },
        {
            label: '',
            field: 'label',
            isDisplayed: true,
            render: (item: DisplayedValidator) =>
                item.default ? (
                    <Box style={celStyle}>
                        <InformationChip label="Par défaut" />
                    </Box>
                ) : null,
        },
        {
            label: 'Actions',
            displayLabel: false,
            isDisplayed: true,
            render: (item: DisplayedValidator) => (
                <TableRowActions
                    itemId={item.id as unknown as string}
                    removeItem={() => {
                        onRemoveItem(item);
                    }}
                />
            ),
        },
    ];

    const handleShowItem = (item: DisplayedValidator) => {
        if (onShowItem) {
            const validator = formalityValidators.find(
                (fv) => (fv.validator as unknown as number) === item.id,
            );
            if (validator) {
                onShowItem(validator as unknown as FormalityValidator);
            }
        }
    };

    return (
        <Card>
            <StaticDatatable
                data={rows}
                columns={columns}
                totalItemsCount={formality?.validators?.length || 0}
                handleClickRow={handleShowItem}
            />
        </Card>
    );
};

export default FormalityValidatorsCard;
